import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, updateDoc, doc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useUser } from '../context/UserContext';
import Swal from 'sweetalert2';

interface PropertyAssignmentManagerProps {
  propertyId: string;
  currentAsesorIds?: string[];
  currentAsociadoIds?: string[];
  onAssignmentChange?: () => void;
}

export default function PropertyAssignmentManager({
  propertyId,
  currentAsesorIds = [],
  currentAsociadoIds = [],
  onAssignmentChange
}: PropertyAssignmentManagerProps) {
  const { user } = useUser();
  const [asesores, setAsesores] = useState<any[]>([]);
  const [asociados, setAsociados] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        // Cargar asesores activos
        const asesorSnap = await getDocs(
          query(collection(db, 'asesor'), where('activo', '==', true))
        );
        const asesorData = asesorSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAsesores(asesorData);

        // Cargar asociados activos
        const asociadoSnap = await getDocs(
          query(collection(db, 'asociado'), where('activo', '==', true))
        );
        const asociadoData = asociadoSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAsociados(asociadoData);

      } catch (error) {
        console.error('Error cargando agentes:', error);
        Swal.fire('Error', 'No se pudieron cargar los agentes disponibles.', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchAgents();
  }, []);

  // Solo admins pueden usar este componente
  if (!user || !user.tipo?.includes('admin')) {
    return (
      <div className="alert alert-danger">
        <h6>Acceso Denegado</h6>
        <p>Solo administradores pueden gestionar asignaciones de propiedades.</p>
      </div>
    );
  }

  const handleAssignAsesor = async (asesorId: string, asesorBusinessId: string) => {
    setSaving(true);
    try {
      const propRef = doc(db, 'propiedades', propertyId);
      
      // Verificar si ya está asignado
      const isCurrentlyAssigned = currentAsesorIds.includes(asesorBusinessId);
      
      if (isCurrentlyAssigned) {
        // Remover asignación
        await updateDoc(propRef, {
          asesor_id: arrayRemove(asesorBusinessId)
        });
        
        // También remover de la propiedad_id del asesor
        const asesorQuery = query(
          collection(db, 'asesor'), 
          where('asesor_id', '==', asesorBusinessId)
        );
        const asesorSnap = await getDocs(asesorQuery);
        if (!asesorSnap.empty) {
          const asesorRef = asesorSnap.docs[0].ref;
          await updateDoc(asesorRef, {
            propiedad_id: arrayRemove(propertyId)
          });
        }
        
        Swal.fire('Éxito', 'Asesor removido de la propiedad.', 'success');
      } else {
        // Agregar asignación
        await updateDoc(propRef, {
          asesor_id: arrayUnion(asesorBusinessId)
        });
        
        // También agregar a la propiedad_id del asesor
        const asesorQuery = query(
          collection(db, 'asesor'), 
          where('asesor_id', '==', asesorBusinessId)
        );
        const asesorSnap = await getDocs(asesorQuery);
        if (!asesorSnap.empty) {
          const asesorRef = asesorSnap.docs[0].ref;
          await updateDoc(asesorRef, {
            propiedad_id: arrayUnion(propertyId)
          });
        }
        
        Swal.fire('Éxito', 'Asesor asignado a la propiedad.', 'success');
      }
      
      onAssignmentChange?.();
      
    } catch (error) {
      console.error('Error asignando asesor:', error);
      Swal.fire('Error', 'No se pudo completar la asignación.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAssignAsociado = async (asociadoId: string, asociadoBusinessId: string) => {
    setSaving(true);
    try {
      const propRef = doc(db, 'propiedades', propertyId);
      
      // Verificar si ya está asignado
      const isCurrentlyAssigned = currentAsociadoIds.includes(asociadoBusinessId);
      
      if (isCurrentlyAssigned) {
        // Remover asignación
        await updateDoc(propRef, {
          asociado_id: arrayRemove(asociadoBusinessId)
        });
        
        // También remover de la propiedad_id del asociado
        const asociadoQuery = query(
          collection(db, 'asociado'), 
          where('asociado_id', '==', asociadoBusinessId)
        );
        const asociadoSnap = await getDocs(asociadoQuery);
        if (!asociadoSnap.empty) {
          const asociadoRef = asociadoSnap.docs[0].ref;
          await updateDoc(asociadoRef, {
            propiedad_id: arrayRemove(propertyId)
          });
        }
        
        Swal.fire('Éxito', 'Asociado removido de la propiedad.', 'success');
      } else {
        // Agregar asignación
        await updateDoc(propRef, {
          asociado_id: arrayUnion(asociadoBusinessId)
        });
        
        // También agregar a la propiedad_id del asociado
        const asociadoQuery = query(
          collection(db, 'asociado'), 
          where('asociado_id', '==', asociadoBusinessId)
        );
        const asociadoSnap = await getDocs(asociadoQuery);
        if (!asociadoSnap.empty) {
          const asociadoRef = asociadoSnap.docs[0].ref;
          await updateDoc(asociadoRef, {
            propiedad_id: arrayUnion(propertyId)
          });
        }
        
        Swal.fire('Éxito', 'Asociado asignado a la propiedad.', 'success');
      }
      
      onAssignmentChange?.();
      
    } catch (error) {
      console.error('Error asignando asociado:', error);
      Swal.fire('Error', 'No se pudo completar la asignación.', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-3">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="property-assignment-manager">
      <h5 className="mb-3">
        <i className="fas fa-user-cog me-2"></i>
        Gestionar Asignaciones
      </h5>
      
      {/* Sección de Asesores */}
      <div className="mb-4">
        <h6 className="text-primary">
          <i className="fas fa-user-tie me-2"></i>
          Asesores ({currentAsesorIds.length} asignados)
        </h6>
        <div className="row g-2">
          {asesores.map(asesor => {
            const isAssigned = currentAsesorIds.includes(asesor.asesor_id);
            return (
              <div key={asesor.id} className="col-md-6">
                <div className={`card ${isAssigned ? 'border-success bg-light' : 'border-light'}`}>
                  <div className="card-body py-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <small className="fw-bold">{asesor.nombre}</small>
                        <br />
                        <small className="text-muted">{asesor.correo}</small>
                      </div>
                      <button
                        className={`btn btn-sm ${isAssigned ? 'btn-outline-danger' : 'btn-outline-success'}`}
                        onClick={() => handleAssignAsesor(asesor.id, asesor.asesor_id)}
                        disabled={saving}
                      >
                        {isAssigned ? (
                          <>
                            <i className="fas fa-minus me-1"></i>
                            Remover
                          </>
                        ) : (
                          <>
                            <i className="fas fa-plus me-1"></i>
                            Asignar
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Sección de Asociados */}
      <div className="mb-4">
        <h6 className="text-info">
          <i className="fas fa-users me-2"></i>
          Asociados ({currentAsociadoIds.length} asignados)
        </h6>
        <div className="row g-2">
          {asociados.map(asociado => {
            const isAssigned = currentAsociadoIds.includes(asociado.asociado_id);
            return (
              <div key={asociado.id} className="col-md-6">
                <div className={`card ${isAssigned ? 'border-info bg-light' : 'border-light'}`}>
                  <div className="card-body py-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <small className="fw-bold">{asociado.nombre}</small>
                        <br />
                        <small className="text-muted">{asociado.correo}</small>
                      </div>
                      <button
                        className={`btn btn-sm ${isAssigned ? 'btn-outline-danger' : 'btn-outline-info'}`}
                        onClick={() => handleAssignAsociado(asociado.id, asociado.asociado_id)}
                        disabled={saving}
                      >
                        {isAssigned ? (
                          <>
                            <i className="fas fa-minus me-1"></i>
                            Remover
                          </>
                        ) : (
                          <>
                            <i className="fas fa-plus me-1"></i>
                            Asignar
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {saving && (
        <div className="alert alert-info">
          <i className="fas fa-spinner fa-spin me-2"></i>
          Guardando cambios...
        </div>
      )}
    </div>
  );
}